import { URLS } from "@core/shared/utils/urls";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import type { Grade, GradeColor } from "@core/grades/types";

interface GradeCardProps {
  grade: Grade;
  index?: number;
}

// Map GradeColor to Tailwind gradient classes
const getGradeColorGradient = (color: GradeColor): string => {
  const colorMap: Record<GradeColor, string> = {
    red: "from-red-500 to-red-600",
    green: "from-green-500 to-green-600",
    blue: "from-blue-500 to-blue-600",
    orange: "from-orange-500 to-orange-600",
    yellow: "from-yellow-500 to-yellow-600",
    purple: "from-purple-500 to-purple-600",
    pink: "from-pink-500 to-pink-600",
    gray: "from-gray-500 to-gray-600",
    black: "from-gray-800 to-gray-900",
    white: "from-gray-100 to-gray-200",
    teal: "from-teal-500 to-teal-600",
    lime: "from-lime-500 to-lime-600",
    indigo: "from-indigo-500 to-indigo-600",
    peach: "from-orange-300 to-pink-300",
    coral: "from-pink-400 to-orange-400",
    maroon: "from-red-800 to-red-900",
    violet: "from-violet-500 to-violet-600",
    brown: "from-amber-700 to-orange-800"
  };
  
  return colorMap[color] || "from-gray-500 to-gray-600";
};

export function GradeCard({ grade, index = 0 }: GradeCardProps) {
  const gradeColor = getGradeColorGradient(grade.color);

  return (
    <div className="flex items-stretch h-full animate-fade-in-up" style={{ animationDelay: `${index * 150}ms` }}>
      {/* Large Grade Number outside the card with floating animation - hidden on mobile */}
      <div className="hidden sm:flex items-center justify-center w-20 lg:w-24 flex-shrink-0 relative">
        <div className={`text-7xl lg:text-8xl font-bold bg-gradient-to-r ${gradeColor} bg-clip-text text-transparent drop-shadow-lg animate-bounce-gentle hover:scale-110 transition-transform duration-300`}>
          {grade.number}
        </div>
        {/* Floating particles around number */}
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute top-2 right-2 w-2 h-2 bg-gradient-to-r ${gradeColor} rounded-full animate-pulse opacity-60`}></div>
          <div className={`absolute bottom-4 left-1 w-1 h-1 bg-gradient-to-r ${gradeColor} rounded-full animate-ping opacity-40`}></div>
          <div className={`absolute top-1/2 left-0 w-1.5 h-1.5 bg-gradient-to-r ${gradeColor} rounded-full animate-bounce opacity-50`}></div>
        </div>
      </div>
      
      {/* Card with enhanced animations */}
      <Link
        to={URLS.grades.view(grade)}
        className="group relative overflow-hidden rounded-3xl bg-white/40 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:rotate-1 flex-1"
        style={{
          animationDelay: `${index * 150}ms`
        }}
      >
        {/* Animated background shapes - smaller on mobile */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating geometric shapes */}
          <div className={`absolute top-4 right-4 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r ${gradeColor} rounded-full opacity-20 animate-float`}></div>
          <div className={`absolute bottom-8 left-6 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r ${gradeColor} rotate-45 opacity-15 animate-spin-slow`}></div>
          <div className={`absolute top-1/2 left-4 w-4 h-4 sm:w-6 sm:h-6 bg-gradient-to-r ${gradeColor} rounded-full opacity-10 animate-bounce-slow`}></div>
        </div>

        {/* Gradient overlay on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradeColor} opacity-0 transition-opacity duration-500 group-hover:opacity-10`} />

        {/* Content - only vertical padding on mobile, full padding on larger screens */}
        <div className="relative py-4 px-0 sm:p-6 h-full flex flex-col items-center justify-center z-10">
          {/* Image or Generated Content - much larger on mobile */}
          {grade.image ? (
            <div className="relative mb-3 sm:mb-4 group/image">
              <div className="w-32 h-32 sm:w-20 sm:h-20 mx-auto rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                <img
                  src={grade.image.url}
                  alt={grade.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Enhanced decorative elements */}
              <div className={`absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r ${gradeColor} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-125 animate-ping`} />
              <div className={`absolute -bottom-1 -left-1 w-4 h-4 bg-gradient-to-r ${gradeColor} rounded-full opacity-0 group-hover:opacity-80 transition-all duration-700 animate-pulse`} />
            </div>
          ) : (
            <div className="relative mb-3 sm:mb-4 group/image">
              <div className={`w-32 h-32 sm:w-20 sm:h-20 mx-auto rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 bg-gradient-to-br ${gradeColor} flex items-center justify-center`}>
                {/* Small grade number as backup - larger on mobile */}
                <div className="text-4xl sm:text-2xl font-bold text-white drop-shadow-lg">
                  {grade.number}
                </div>
              </div>
              {/* Enhanced decorative elements */}
              <div className={`absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r ${gradeColor} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-125 animate-ping`} />
              <div className={`absolute -bottom-1 -left-1 w-4 h-4 bg-gradient-to-r ${gradeColor} rounded-full opacity-0 group-hover:opacity-80 transition-all duration-700 animate-pulse`} />
            </div>
          )}
          
          {/* Grade name with enhanced styling - much larger text on mobile */}
          <h3 className={`text-xl sm:text-xl font-bold bg-gradient-to-r ${gradeColor} bg-clip-text text-transparent text-center mb-2 group-hover:scale-105 transition-transform duration-300`}>
            {grade.name}
          </h3>
          
          {/* Lesson count with enhanced icon - larger on mobile */}
          {grade.total?.lessons > 0 && (
            <div className="flex items-center justify-center space-x-2 text-sm sm:text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-300">
              <div className="relative">
                <svg className="w-5 h-5 sm:w-4 sm:h-4 text-indigo-500 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {/* Sparkle effect */}
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
              </div>
              <span>{trans("grades.totalLessons", { count: grade.total.lessons })}</span>
            </div>
          )}

          {/* Enhanced hover indicator */}
          <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${gradeColor} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />
          
          {/* Fun emoji on hover */}
          <div className="absolute top-2 right-2 text-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 group-hover:translate-x-0">
            🎓
          </div>
        </div>
      </Link>
    </div>
  );
}